import type { DroughtFrequencyRow } from '../../analytics/drought-frequency.ts';
import { formatDecimal, formatInteger, formatPercent } from '../../shared/format.ts';

export function DroughtHistogram(props: {
  rows: readonly DroughtFrequencyRow[];
  median: number | null;
  p90: number | null;
  p95: number | null;
  currentDrought: number | null;
  view: 'all' | 'tail';
  tailFrom: number;
  showIndependentReference: boolean;
}) {
  const visible = props.rows.filter((row) => (props.view === 'all' ? true : row.droughtLength >= props.tailFrom));
  if (visible.length === 0) {
    return <p className="text-sm text-muted">No completed drought lengths in this view.</p>;
  }
  const minX = visible[0]?.droughtLength ?? 1;
  const maxX = visible[visible.length - 1]?.droughtLength ?? minX;
  const maxCount = Math.max(
    1,
    ...visible.map((row) => row.occurrenceCount),
    ...(props.showIndependentReference ? visible.map((row) => row.expectedIndependentCount ?? 0) : [0]),
  );
  const barWidth = 14;
  const left = 36;
  const top = 12;
  const height = 180;
  const plotWidth = (maxX - minX + 1) * barWidth;
  const width = left + plotWidth + 8;
  const xFor = (length: number) => left + (length - minX + 0.5) * barWidth;
  const inRange = (length: number | null) => length !== null && length >= minX && length <= maxX;
  const tickEvery = Math.max(1, Math.ceil((maxX - minX + 1) / 12));

  return (
    <div className="overflow-x-auto">
      <svg
        role="img"
        aria-label="Drought length distribution"
        width={width}
        height={top + height + 28}
        className="text-ink"
      >
        <title>Drought length distribution</title>
        <desc>
          Bar chart of completed drought counts by integer drought length. Zero-count lengths inside the observed range
          are included.
        </desc>
        <line x1={left} y1={top} x2={left} y2={top + height} stroke="currentColor" strokeOpacity="0.25" />
        <line
          x1={left}
          y1={top + height}
          x2={left + plotWidth}
          y2={top + height}
          stroke="currentColor"
          strokeOpacity="0.25"
        />
        <text x={4} y={top + 8} className="fill-muted" fontSize="10">
          {formatInteger(maxCount)}
        </text>
        {visible.map((row) => {
          const x = left + (row.droughtLength - minX) * barWidth;
          const barHeight = (row.occurrenceCount / maxCount) * height;
          const expectedHeight =
            props.showIndependentReference && row.expectedIndependentCount !== null
              ? (row.expectedIndependentCount / maxCount) * height
              : null;
          return (
            <g key={row.droughtLength}>
              <rect
                x={x + 1}
                y={top + height - barHeight}
                width={barWidth - 2}
                height={barHeight}
                fill="#3f5d56"
                fillOpacity={row.occurrenceCount === 0 ? 0 : 0.82}
              >
                <title>
                  {`Drought: ${row.droughtLength} rounds\nOccurrences: ${formatInteger(row.occurrenceCount)}\nShare: ${formatPercent(row.shareOfCompleted, 1)}\nReached at least ${row.droughtLength}: ${formatInteger(row.reachedAtLeastCount)}\nTail share: ${formatPercent(row.reachedAtLeastShare, 1)}${
                    props.showIndependentReference && row.expectedIndependentCount !== null
                      ? `\nIndependent-reference expected: ${formatDecimal(row.expectedIndependentCount, 2)}`
                      : ''
                  }`}
                </title>
              </rect>
              {expectedHeight !== null ? (
                <rect
                  x={x + 3}
                  y={top + height - expectedHeight}
                  width={barWidth - 6}
                  height={Math.max(expectedHeight > 0 ? 1 : 0, 0)}
                  fill="none"
                  stroke="#8a5a28"
                  strokeWidth="1.25"
                  pointerEvents="none"
                />
              ) : null}
              {row.droughtLength === minX ||
              row.droughtLength === maxX ||
              (row.droughtLength - minX) % tickEvery === 0 ? (
                <text x={x + barWidth / 2} y={top + height + 14} textAnchor="middle" className="fill-muted" fontSize="9">
                  {row.droughtLength}
                </text>
              ) : null}
            </g>
          );
        })}
        <ReferenceLine x={xFor(props.median ?? 0)} label="Median" y={top} height={height} show={inRange(props.median)} />
        <ReferenceLine x={xFor(props.p90 ?? 0)} label="P90" y={top} height={height} show={inRange(props.p90)} />
        <ReferenceLine x={xFor(props.p95 ?? 0)} label="P95" y={top} height={height} show={inRange(props.p95)} />
        <ReferenceLine
          x={xFor(props.currentDrought ?? 0)}
          label="Current"
          y={top}
          height={height}
          show={inRange(props.currentDrought)}
          dashed
        />
      </svg>
      <p className="mt-1 text-xs text-muted">X: drought length · Y: completed occurrences</p>
    </div>
  );
}

function ReferenceLine(props: {
  x: number;
  y: number;
  height: number;
  label: string;
  show: boolean;
  dashed?: boolean;
}) {
  if (!props.show) {
    return null;
  }
  return (
    <g>
      <line
        x1={props.x}
        y1={props.y}
        x2={props.x}
        y2={props.y + props.height}
        stroke="#8a5a28"
        strokeOpacity="0.7"
        strokeDasharray={props.dashed ? '4 3' : undefined}
      />
      <text x={props.x + 3} y={props.y + 10} fontSize="9" className="fill-muted">
        {props.label}
      </text>
    </g>
  );
}
